import { Popover, Portal, useDisclosure } from '@chakra-ui/react';
import { AssociationEntity, IntegrationLogResponse } from '@monetize/types/app';
import { useCallback, useRef, useState } from 'react';
import { MdError } from 'react-icons/md';
import {
  IntegrationLogDetailModal,
  useCrmRecordLinkConfig,
} from '../IntegrationLog';
import { MBox, MHStack, MIcon, MTooltip } from '../Monetize';
import { EXTERNAL_SYSTEM_ICONS } from './externalSystemIcons';
import { IntegrationInfoPopoverContent } from './IntegrationInfoPopoverContent';
import {
  IntegrationSystemSummary,
  useIntegrationInfo,
} from './useIntegrationInfo';

interface ExternalAssociationsIconsProps {
  entity: AssociationEntity;
  recordId: string;
  showWarningIfNotAssociated?: boolean;
}

// The two states cannot co-occur: a record is only reported as not connected
// when it has no sync log at all, and a failure needs one.
const getTooltipLabel = ({
  label,
  hasFailure,
  isNotConnected,
}: IntegrationSystemSummary) => {
  if (isNotConnected) {
    return `Not Connected to ${label}`;
  }
  return hasFailure ? `${label} — last sync failed` : label;
};

export const ExternalAssociationsIcons = ({
  entity,
  recordId,
  showWarningIfNotAssociated = false,
}: ExternalAssociationsIconsProps) => {
  const { open: isOpen, onClose, onOpen } = useDisclosure();
  const [selectedLog, setSelectedLog] = useState<IntegrationLogResponse | null>(
    null,
  );

  const { systems, isLoading, isFetching, refetch } = useIntegrationInfo({
    entity,
    recordId,
    showWarningIfNotAssociated,
    // A sync can land while someone is reading the popover.
    pollForUpdates: isOpen,
  });
  const externalRecordLinkConfig = useCrmRecordLinkConfig();

  // Selecting a log swaps the popover for the details modal rather than nesting
  // one portalled overlay inside another. The modal waits for the popover to
  // finish closing: zag drops the popover's dismissable layer a tick after
  // `open` flips, and dismisses any layer opened above one it drops — so a
  // modal mounted in the same commit would close itself immediately.
  const pendingLogRef = useRef<IntegrationLogResponse | null>(null);

  const handleSelectLog = useCallback(
    (log: IntegrationLogResponse) => {
      pendingLogRef.current = log;
      onClose();
    },
    [onClose],
  );

  const handlePopoverExitComplete = useCallback(() => {
    if (pendingLogRef.current) {
      setSelectedLog(pendingLogRef.current);
      pendingLogRef.current = null;
    }
  }, []);

  const handleCloseDetail = useCallback(() => setSelectedLog(null), []);

  // Syncs happen server-side after the page loads, so what was cached at mount
  // is routinely out of date by the time someone opens the popover.
  const handleOpen = useCallback(() => {
    onOpen();
    refetch();
  }, [onOpen, refetch]);

  if (isLoading || systems.length === 0) {
    return null;
  }

  return (
    <>
      <MHStack gap={1} ml={2}>
        <Popover.Root
          open={isOpen}
          lazyMount
          unmountOnExit
          onExitComplete={handlePopoverExitComplete}
          onOpenChange={(e) => {
            if (e.open) {
              handleOpen();
            } else {
              onClose();
            }
          }}
          positioning={{
            placement: 'bottom-start',
          }}
        >
          <Popover.Trigger asChild>
            <MHStack
              asChild
              gap={1}
              cursor="pointer"
              aria-label="External connections"
              // Plain hover selector, not Chakra's role="group" convention —
              // this trigger is a real <button> and role="group" would hide
              // that from assistive tech.
              css={{ '&:hover [data-icon-slot]': { bg: 'bg.hover' } }}
            >
              <button type="button">
                {systems.map((summary) => {
                  const { system, hasFailure, isNotConnected } = summary;
                  return (
                    <MTooltip key={system} label={getTooltipLabel(summary)}>
                      <MBox
                        data-icon-slot
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        position="relative"
                        borderRadius="full"
                        p="2px"
                        transition="background 0.15s"
                        {...(isNotConnected && {
                          borderWidth: '2px',
                          borderStyle: 'solid',
                          borderColor: 'tOrange.base',
                          'data-testid': `crm-warning-${system}`,
                        })}
                      >
                        <MIcon
                          as={EXTERNAL_SYSTEM_ICONS[system]}
                          boxSize="18px"
                          opacity={isNotConnected ? 0.6 : 1}
                        />
                        {/* The orange ring reports connection state; a failed sync
                            is a separate problem and gets its own red badge, so a
                            record can show both at once. */}
                        {hasFailure && (
                          <MIcon
                            as={MdError}
                            color="tRed.base"
                            // Fills the glyph's cut-out "!" so it stays legible
                            // over the system logo underneath.
                            bg="bg.surface"
                            borderRadius="full"
                            boxSize="11px"
                            position="absolute"
                            top="-2px"
                            right="-2px"
                            aria-hidden
                            data-testid={`sync-failure-${system}`}
                          />
                        )}
                      </MBox>
                    </MTooltip>
                  );
                })}
              </button>
            </MHStack>
          </Popover.Trigger>
          {isOpen && (
            <Portal>
              <IntegrationInfoPopoverContent
                systems={systems}
                recordId={recordId}
                isRefreshing={isFetching}
                onRefresh={refetch}
                onSelectLog={handleSelectLog}
              />
            </Portal>
          )}
        </Popover.Root>
      </MHStack>

      {/* Mounted on demand — this component sits in every record page header,
          and the modal pulls in the resync mutation and lookup popovers. */}
      {selectedLog && (
        <IntegrationLogDetailModal
          isOpen
          logItem={selectedLog}
          onClose={handleCloseDetail}
          externalRecordLinkConfig={externalRecordLinkConfig}
        />
      )}
    </>
  );
};
